"use client";

import api from "@/app/api";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Copy, Info, Loader2 } from "lucide-react";
import { useSearchParams } from "next/navigation";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";

const GenerateShareableLinkButton = () => {
  const search = useSearchParams();
  const [shareUrl, setShareUrl] = useState<string | null>(null);
  const [expiresAt, setExpiresAt] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const lastSearchKeyRef = useRef<string>("");

  const searchKey = `${search.get("data")}|${search.get("metadata")}|${search.get("tripType")}`;

  const ensureShareLink = useCallback(async (): Promise<string | null> => {
    const searchData = JSON.parse(search.get("data") || "{}");
    const metadata = JSON.parse(search.get("metadata") || "[]");
    const tripType = search.get("tripType");

    if (!tripType || !searchData?.routeInfos?.length) {
      setShareUrl(null);
      setExpiresAt(null);
      return null;
    }

    try {
      setIsLoading(true);
      const response = await api.post<{
        uid: string;
        url: string;
        expiresAt: string;
        status: string;
        isExisting?: boolean;
      }>("/flight/shareable-links", {
        searchQuery: searchData,
        metadata,
        tripType,
      });

      setShareUrl(response.data.url);
      setExpiresAt(response.data.expiresAt);
      return response.data.url;
    } catch (error: any) {
      setShareUrl(null);
      setExpiresAt(null);
      const message =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to prepare shareable link";
      toast.error(message);
      return null;
    } finally {
      setIsLoading(false);
    }
  }, [search]);

  useEffect(() => {
    if (!searchKey || searchKey === "||") return;
    if (lastSearchKeyRef.current === searchKey) return;

    lastSearchKeyRef.current = searchKey;
    ensureShareLink();
  }, [searchKey, ensureShareLink]);

  const handleCopy = async () => {
    const url = shareUrl ?? (await ensureShareLink());
    if (!url) return;

    try {
      await navigator.clipboard.writeText(url);
      const expiryLabel = expiresAt
        ? new Date(expiresAt).toLocaleString()
        : "24 hours";
      toast.success(`Link copied! Expires: ${expiryLabel}`);
    } catch {
      toast.error("Failed to copy link to clipboard");
    }
  };

  if (!search.get("tripType") || !search.get("data")) {
    return null;
  }

  return (
    <div className="flex items-center gap-1">
      <button
        type="button"        
        onClick={handleCopy}
        disabled={isLoading || !shareUrl}
        className="flex items-center gap-2 border-primary text-primary hover:bg-primary/5"
      >
        {isLoading ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : (
          <Copy className="h-4 w-4" />
        )}
        <span className="hidden sm:inline">Copy Shareable Link</span>
        <span className="sm:hidden">Copy Link</span>
      </button>
      <TooltipProvider delayDuration={200}>
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              className="inline-flex items-center justify-center rounded-full p-1.5 text-muted-foreground hover:text-primary hover:bg-primary/5 transition-colors"
              aria-label="Shareable link information"
            >
              <Info className="h-4 w-4" />
            </button>
          </TooltipTrigger>
          <TooltipContent side="bottom" className="max-w-[280px]">
            <p>
              Creates one link per search, valid for 24 hours. Recipients can
              view flights only — no booking or filters.
            </p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    </div>
  );
};

export default GenerateShareableLinkButton;
